import { useMemo, useState } from "react";
import { Building2, ChevronsUpDown, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { normName } from "./contractCreateShared";

export const CounterpartyField = ({ value, onChange, clubs, tf, innerRef, highlight }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const isClub = useMemo(() => clubs.some((c) => normName(c.name) === normName(value)), [clubs, value]);
  const exact = q.trim() && clubs.some((c) => normName(c.name) === normName(q));

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(""); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          ref={innerRef}
          data-testid="contract-counterparty-input"
          className={cn(
            "flex h-11 sm:h-10 w-full min-w-0 items-center gap-2 rounded-md border border-input bg-transparent px-3 text-sm shadow-sm transition-colors motion-reduce:transition-none",
            "focus:outline-none focus-visible:ring-1 focus-visible:ring-ring hover:bg-foreground/[0.03]",
            highlight && "ring-2 ring-red-500/40 border-red-500/50"
          )}
        >
          <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className={cn("flex-1 truncate text-left", !value && "text-muted-foreground")}>
            {value || tf("Aucune contrepartie")}
          </span>
          {value && (
            <span className={cn("text-[10px] px-1.5 py-0.5 rounded-full border shrink-0",
              isClub ? "border-emerald-500/30 text-emerald-500 bg-emerald-500/10" : "border-border dark:border-white/[0.12] text-muted-foreground")}>
              {isClub ? tf("Club") : tf("Externe")}
            </span>
          )}
          <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput value={q} onValueChange={setQ} placeholder={tf("Rechercher un club…")} data-testid="counterparty-search-input" />
          <CommandList className="max-h-56">
            <CommandEmpty>{tf("Aucun club trouvé")}</CommandEmpty>
            {clubs.length > 0 && (
              <CommandGroup>
                {clubs.map((c) => (
                  <CommandItem key={c.id} value={c.name} data-testid={`counterparty-club-${c.id}`}
                    onSelect={() => { onChange(c.name); setOpen(false); }}>
                    <Building2 className="h-3.5 w-3.5 mr-2 text-muted-foreground" />
                    <span className="flex-1 truncate">{c.name}</span>
                    {normName(c.name) === normName(value) && <Check className="h-3.5 w-3.5 text-emerald-500" />}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {q.trim() && !exact && (
              <CommandGroup>
                <CommandItem value={q} data-testid="counterparty-external-option"
                  onSelect={() => { onChange(q.trim()); setOpen(false); }}>
                  <span className="text-sm">{tf("Utiliser")} « {q.trim()} » {tf("comme contrepartie externe")}</span>
                </CommandItem>
              </CommandGroup>
            )}
            {value && !q.trim() && (
              <CommandGroup>
                <CommandItem value="__clear" data-testid="counterparty-clear-option"
                  onSelect={() => { onChange(""); setOpen(false); }}>
                  <span className="text-sm text-muted-foreground">{tf("Aucune contrepartie")}</span>
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
